import { CanopyProvider, ControlPanel } from '@bloomfieldrobotics/canopy';
import { ArrowBack } from '@mui/icons-material';
import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import LoadingPlant from '../commonUI/LoadingPlant/LoadingPlant';
import { CollectionPointsMap } from './components/CollectionPointsMap/CollectionPointsMap';
import { CollectionBlocksControlSection } from './components/ControlSections/CollectionBlocksControlSection';
import { useCollectionDetails } from './hooks/useCollectionDetails';
/**
 * Will display the points on the map and the Control Panel
 */
export const CollectionDetailsPage = () => {
  // Get the collectionId param from the URL
  const { collectionId } = useParams<{ collectionId: string }>();
  /**
   * Get the details for the Collection
   */
  const {
    metadata,
    points,
    pointsLoading,
    blocks,
    blocksLoading,
    detailsLoading,
  } = useCollectionDetails(collectionId);

  const hasPoints = !pointsLoading && points && points.length > 0;
  const hasBlocks = !blocksLoading && blocks && blocks.length > 0;

  const [hiddenBlockIds, setHiddenBlockIds] = useState<string[]>([]);
  return (
    <CanopyProvider>
      <Stack direction='row' sx={{ flexGrow: 1, height: '100vh' }}>
        <ControlPanel>
          <Stack direction='row' sx={{ alignItems: 'center' }}>
            <GoBackButton />
            {metadata?.collectionDate && (
              <Typography fontSize={16} fontWeight='bold'>
                {format(parseISO(metadata.collectionDate), 'MM/dd')}
              </Typography>
            )}
          </Stack>
          <Divider flexItem />
          {metadata?.id && (
            <CollectionBlocksControlSection
              collectionId={metadata.id}
              onBlockToggle={setHiddenBlockIds}
            />
          )}
        </ControlPanel>
        {metadata?.id && (
          <Stack direction='column' spacing={2} sx={{ flexGrow: 1 }}>
            {detailsLoading ? (
              <LoadingPlant />
            ) : hasPoints && hasBlocks ? (
              <CollectionPointsMap
                points={points}
                blocks={blocks}
                hiddenBlockIds={hiddenBlockIds}
              />
            ) : (
              <NoPointsDisplay />
            )}
          </Stack>
        )}
      </Stack>
    </CanopyProvider>
  );
};

const NoPointsDisplay = () => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        flexGrow: 1,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Typography fontSize={24}>{t('No Points to display')}</Typography>
    </Box>
  );
};

const GoBackButton = () => {
  const navigate = useNavigate();
  const goBack = () => {
    /**
     * The `/collections/:collectionId` route does not live as a direct child
     * of the `/collections` route, so we use a relative path navigation.
     *
     * See https://reactrouter.com/6.30.0/hooks/use-navigate#optionsrelative
     * for more information on how this works
     */
    navigate('..', { relative: 'path' });
  };

  return (
    <IconButton onClick={goBack}>
      <ArrowBack />
    </IconButton>
  );
};
